import { Injectable } from '@angular/core';
import { LOAN_CONSTRAINTS, LoanConstraints, ValidationResult, PrepaymentFieldError } from './models';

@Injectable({ providedIn: 'root' })
export class ValidationService {

  validateLoanForm(fields: {
    loanAmount: number | null;
    annualInterestRate: number | null;
    loanTermYears: number | null;
    loanStartDate: string;
    borrowerAge: number | null;
    monthlyIncome: number | null;
  }, constraints: LoanConstraints = LOAN_CONSTRAINTS): ValidationResult {
    const C = constraints;
    const errors: Record<string, string> = {};

    // Loan amount
    const amt = fields.loanAmount;
    if (amt === null || amt === undefined || isNaN(Number(amt))) {
      errors['loanAmount'] = 'Loan amount is required.';
    } else if (amt <= 0) {
      errors['loanAmount'] = 'Loan amount must be greater than ₹0.';
    } else if (amt < C.amount.min) {
      errors['loanAmount'] = `Minimum loan amount is ${this.fmtINR(C.amount.min)}.`;
    } else if (amt > C.amount.max) {
      errors['loanAmount'] = `Maximum loan amount is ${this.fmtINR(C.amount.max)}.`;
    }

    // Interest rate
    const rate = fields.annualInterestRate;
    if (rate === null || rate === undefined || isNaN(Number(rate))) {
      errors['annualInterestRate'] = 'Interest rate is required.';
    } else if (rate <= 0) {
      errors['annualInterestRate'] = 'Interest rate must be greater than 0%.';
    } else if (rate < C.rate.min) {
      errors['annualInterestRate'] = `Minimum interest rate is ${C.rate.min}%.`;
    } else if (rate > C.rate.max) {
      errors['annualInterestRate'] = `Maximum interest rate is ${C.rate.max}%.`;
    }

    // Loan term
    const term = fields.loanTermYears;
    if (term === null || term === undefined || isNaN(Number(term))) {
      errors['loanTermYears'] = 'Loan term is required.';
    } else if (term <= 0) {
      errors['loanTermYears'] = 'Loan term must be greater than 0 years.';
    } else if (!Number.isInteger(Number(term))) {
      errors['loanTermYears'] = 'Loan term must be a whole number of years.';
    } else if (term < C.term.min) {
      errors['loanTermYears'] = `Minimum term is ${C.term.min} year.`;
    } else if (term > C.term.max) {
      errors['loanTermYears'] = `Maximum term is ${C.term.max} years.`;
    }

    // Start date
    if (!fields.loanStartDate) {
      errors['loanStartDate'] = 'Start date is required.';
    } else {
      const parsed = new Date(fields.loanStartDate + '-01');
      if (isNaN(parsed.getTime())) {
        errors['loanStartDate'] = 'Enter a valid start date.';
      }
    }

    // Borrower age (optional but validated if present)
    const age = fields.borrowerAge;
    if (age !== null && age !== undefined && String(age) !== '') {
      if (age <= 0) {
        errors['borrowerAge'] = 'Age must be greater than 0.';
      } else if (age < C.age.min) {
        errors['borrowerAge'] = `Minimum age is ${C.age.min} years.`;
      } else if (age > C.age.max) {
        errors['borrowerAge'] = `Maximum age is ${C.age.max} years.`;
      } else if (!Number.isInteger(Number(age))) {
        errors['borrowerAge'] = 'Age must be a whole number.';
      }
    }

    // Monthly income (optional but validated if present)
    const income = fields.monthlyIncome;
    if (income !== null && income !== undefined && String(income) !== '') {
      if (income < 0) {
        errors['monthlyIncome'] = 'Income cannot be negative.';
      } else if (income > C.income.max) {
        errors['monthlyIncome'] = `Income value seems too large.`;
      }
    }

    return { valid: Object.keys(errors).length === 0, errors };
  }

  validatePrepayment(
    month: number | null,
    amount: number | null,
    maxMonth: number,
    constraints: LoanConstraints = LOAN_CONSTRAINTS
  ): PrepaymentFieldError {
    const C = constraints;
    const errors: PrepaymentFieldError = {};

    if (month === null || month === undefined || String(month) === '') {
      errors.month = 'Month is required.';
    } else if (month <= 0 || !Number.isInteger(Number(month))) {
      errors.month = 'Enter a valid whole month number.';
    } else if (month < 1) {
      errors.month = 'Month must be at least 1.';
    } else if (month > maxMonth) {
      errors.month = `Month cannot exceed loan tenure (${maxMonth} months).`;
    }

    if (amount === null || amount === undefined || String(amount) === '') {
      errors.amount = 'Amount is required.';
    } else if (amount <= 0) {
      errors.amount = 'Amount must be greater than ₹0.';
    } else if (amount < C.prepayAmount.min) {
      errors.amount = `Minimum prepayment is ₹${C.prepayAmount.min.toLocaleString('en-IN')}.`;
    } else if (amount > C.prepayAmount.max) {
      errors.amount = `Maximum single prepayment is ₹${C.prepayAmount.max.toLocaleString('en-IN')}.`;
    }

    return errors;
  }

  private fmtINR(n: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0
    }).format(n);
  }
}
